/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Avatar,
    AvatarGroup,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Tooltip,
    Typography,
    Zoom,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import TaskChecklistCard from 'src/sections/@dashboard/leadDetail/TaskChecklistCard';
import { TASK_EXIT_EVENT, TASK_RESTORE_EVENT } from 'src/sections/tasks/_shared/taskExitAnimation';
import TemplateDetail from 'src/sections/tasks/templates/TemplateDetail';
import CreateTaskDialog from 'src/sections/tasks/CreateTaskDialog';
import TaskViewer from 'src/sections/tasks/TaskViewer';
import { dispatch, useSelector } from 'src/redux/store';
import { getTemplates } from 'src/redux/slices/taskTemplateSlice';
import { completeTask } from 'src/redux/slices/taskSlice';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import useAuth from 'src/hooks/useAuth';
import { PRIORITY_COLORS, STATUS_COLORS } from 'src/constants/TaskConstants';
import { warning as warningPalette, info as infoPalette, success as successPalette, error as errorPalette, grey as greyPalette } from 'src/theme/palette';
import { callApi } from 'src/utils/apiCall';
import { parseApiDate, formatTaskDate } from 'src/utils/taskDateUtils';
import Iconify from 'src/components/Iconify';
import { interpolateTaskMergeTags } from 'src/utils/taskMergeTags';
import { resolveCompleter } from 'src/utils/taskAttribution';
import { getTaskPauseState } from 'src/utils/taskPauseState';
import { CompleterAttribution, PausedAttribution, RescheduledChip } from 'src/sections/tasks/_shared/TaskAttribution';
import { getChecklistProgress } from './taskChecklistUtils';

// -----------------------------------------------

const MAC = {
    cardBg: '#FFFFFF',
    border: 'rgba(0, 0, 0, 0.06)',
    borderHover: 'rgba(0, 0, 0, 0.10)',
    text: '#1D1D1F',
    textSecondary: '#86868B',
    textTertiary: '#A1A1A6',
    accent: '#007AFF',
    success: '#34C759',
    warning: '#FF9500',
    danger: '#FF3B30',
    shadowSm: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03)',
    shadowMd: '0 2px 4px rgba(0,0,0,0.06), 0 8px 16px rgba(0,0,0,0.04)',
};
const MAC_FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Helvetica Neue", system-ui, sans-serif';

// WCAG AA text colours for chip-style (alpha-0.10 bg) surfaces.
// .main shades all fail 4.5:1 on white; .dark variants pass.
// skyBlue (#078DEE) → info.dark (#006C9C); mintyZest (#00AB55) → success.dark; grey[500] → grey[700].
const STATUS_TEXT_COLORS = {
    pending:     warningPalette.dark,           // #B76E00 — was warning.main #FFAB00 (2.25:1)
    in_progress: infoPalette.dark,              // #006C9C — was skyBlue #078DEE (3.2:1)
    completed:   successPalette.dark,           // #118D57 — was mintyZest #00AB55 (3.5:1)
    archived:    greyPalette[700],              // #454F5B — was grey[500] #919EAB (2.2:1)
};
const PRIORITY_TEXT_COLORS = {
    urgent:  errorPalette.dark,                 // #B71D18 — was tomatoRed #FF4842 (3.4:1)
    high:    warningPalette.dark,               // #B76E00 — was warning.main #FFAB00 (2.25:1)
    normal:  infoPalette.dark,                  // #006C9C — was skyBlue #078DEE (3.2:1)
    low:     greyPalette[700],                  // #454F5B — was grey[500] (2.2:1)
};

// -----------------------------------------------
// AddTaskSchema + AddTaskDialog removed — the "+ New task" surface now reuses
// the shared CreateTaskDialog (src/sections/tasks/CreateTaskDialog.jsx) which
// has the full V1-parity field set (assignees, lead-treatment, etc.).

// -----------------------------------------------

function formatDueLabel(dueAt, status) {
    if (!dueAt) return null;
    const due = parseApiDate(dueAt)?.toDate() || new Date();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
    const diffDays = Math.round((dueDay - today) / 86400000);

    let label;
    if (diffDays === 0) {
        label = `Today ${due.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === -1) {
        label = 'Yesterday';
    } else if (diffDays < 0) {
        label = `${Math.abs(diffDays)} days ago`;
    } else if (diffDays === 1) {
        label = 'Tomorrow';
    } else {
        label = due.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    }

    let color = 'text.primary';
    if (status === 'completed' || status === 'archived') {
        color = 'text.secondary';
    } else if (diffDays < 0) {
        color = 'error.main';
    } else if (diffDays === 0) {
        color = 'success.main';
    }

    return { label, color };
}

// -----------------------------------------------

// Format a paused-until date as a short label (e.g. "12 Jun").
function formatPausedUntil(pausedUntil) {
    if (!pausedUntil) return null;
    const d = parseApiDate(pausedUntil)?.toDate() || new Date(pausedUntil);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function TaskRow({ task, onComplete, onTitleClick, isPaused, pausedInfo, isTemplateTask }) {
    const isCompleted = task.status === 'completed' || task.status === 'archived';
    const pauseState = getTaskPauseState(task);
    const rowIsPaused = Boolean(isPaused || pauseState.isPaused);
    const rowPausedInfo = pausedInfo || {
        pausedUntil: pauseState.pausedUntil,
        pauseSource: pauseState.pauseSource,
    };
    const dueInfo = formatDueLabel(task.due_at, task.status);
    const priorityColor = task.priority ? PRIORITY_COLORS[task.priority] || '#919EAB' : null;
    const priorityTextColor = task.priority ? (PRIORITY_TEXT_COLORS[task.priority] || greyPalette[700]) : null;
    // The list endpoint `/api/v1/practices/{id}/tasks/` returns a pre-computed
    // `checklist_progress` string and DOES NOT include `checklist_items`, so a
    // raw `task.checklist_items.filter(...)` reads `[]` and renders 0/0. The
    // shared helper prefers `checklist_progress` and falls back to
    // recomputing from items when present (detail-shape rows). See fix(#67).
    const checklistProgress = getChecklistProgress(task);
    const checklistDone = checklistProgress?.done ?? 0;
    const checklistTotal = checklistProgress?.total ?? 0;
    const statusColor = task.status ? STATUS_COLORS[task.status] : null;
    const statusTextColor = task.status ? (STATUS_TEXT_COLORS[task.status] || statusColor) : null;

    // Completer attribution — System-aware (falls back to "System" for
    // automated/no-user completions instead of rendering blank). See
    // resolveCompleter in src/utils/taskAttribution.js.
    const completer = resolveCompleter(task);
    const completerName = completer.name;
    // All API dates go through formatTaskDate (handles DRF + ISO) — never new Date().
    const completedDateLabel = formatTaskDate(task.completed_at);
    const showCompleter = isCompleted && completerName && completedDateLabel;
    let assignees = [];
    if (Array.isArray(task.assignments)) assignees = task.assignments;
    else if (Array.isArray(task.assignees)) assignees = task.assignees;

    let rowOpacity = 1;
    if (isCompleted) rowOpacity = 0.55;
    else if (rowIsPaused) rowOpacity = 0.8;

    // Complete toggle.
    // - Completed → filled check.
    // - Paused → pause glyph (muted); resume happens in the slideout, so the
    //   row is non-actionable here and we never hit the wrong endpoint.
    // - Template-checklist task → completes via its checklist items inside the
    //   slideout/TaskBodyView, NOT the task-level /complete/ endpoint. A
    //   complete circle calling onComplete(task.id) would mark the WHOLE task
    //   done and bypass per-item logic, so template rows open the slideout on
    //   click instead of exposing a (wrong) complete button.
    // - Standalone open task → the working complete circle.
    let completeToggle;
    if (isCompleted) {
        completeToggle = <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: MAC.success, width: 18, height: 18, flexShrink: 0 }} />;
    } else if (rowIsPaused) {
        completeToggle = <Iconify icon="eva:pause-circle-outline" sx={{ color: MAC.textTertiary, width: 18, height: 18, flexShrink: 0 }} />;
    } else if (isTemplateTask) {
        completeToggle = <Iconify icon="eva:radio-button-off-outline" sx={{ color: MAC.textTertiary, width: 18, height: 18, flexShrink: 0 }} />;
    } else {
        completeToggle = (
            <Tooltip title="Mark as complete" placement="bottom" TransitionComponent={Zoom}>
                <Box
                    onClick={(e) => { e.stopPropagation(); onComplete(task.id); }}
                    sx={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        border: `1.5px solid ${MAC.textTertiary}`,
                        cursor: 'pointer',
                        flexShrink: 0,
                        transition: 'border-color 150ms ease, background-color 150ms ease',
                        '&:hover': { borderColor: MAC.accent, bgcolor: 'rgba(0, 122, 255, 0.06)' },
                    }}
                />
            </Tooltip>
        );
    }

    // Surface the V1→V2 migrated reminder note when present and not already
    // embedded in the description (which the migration concatenates into).
    const legacyNote = task?.metadata?.legacy_note;
    const description = task?.description || '';
    const showLegacyNote = Boolean(legacyNote) && !description.includes(legacyNote);

    // Trailing meta: paused pill / completer attribution / due-date label.
    // Paused tasks show a pause-tinted "Paused" pill (not the overdue red date).
    let dueLabelColor = MAC.textSecondary;
    if (dueInfo?.color === 'error.main') dueLabelColor = MAC.danger;
    else if (dueInfo?.color === 'success.main') dueLabelColor = MAC.success;

    let dueOrStatusEl = null;
    if (rowIsPaused) {
        dueOrStatusEl = (
            <Typography sx={{ fontFamily: MAC_FONT, fontSize: '11px', fontWeight: 500, color: MAC.warning, whiteSpace: 'nowrap', flexShrink: 0 }}>
                Paused
            </Typography>
        );
    } else if (showCompleter) {
        // [avatar] Name · 18 Jun  (system → gear avatar + "System").
        dueOrStatusEl = <CompleterAttribution task={task} dateLabel={completedDateLabel} color={MAC.success} size={16} />;
    } else if (dueInfo) {
        dueOrStatusEl = (
            <Typography sx={{ fontFamily: MAC_FONT, fontSize: '11px', fontWeight: 500, color: dueLabelColor, whiteSpace: 'nowrap', flexShrink: 0 }}>
                {dueInfo.label}
            </Typography>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                py: '8px',
                px: '8px',
                borderRadius: '8px',
                transition: 'background-color 150ms ease',
                cursor: 'pointer',
                opacity: rowOpacity,
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.025)' },
            }}
            onClick={() => onTitleClick(task)}
        >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
            }}
        >
            {completeToggle}

            {/* Title */}
            <Typography
                sx={{
                    fontFamily: MAC_FONT,
                    flex: 1,
                    minWidth: 0,
                    fontWeight: 400,
                    fontSize: '13px',
                    lineHeight: 1.4,
                    color: isCompleted ? MAC.textSecondary : MAC.text,
                    textDecoration: isCompleted ? 'line-through' : 'none',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    letterSpacing: '-0.01em',
                }}
            >
                {interpolateTaskMergeTags(task.title || '—', task)}
            </Typography>

            {/* Status chip */}
            {statusColor && (
                <Chip
                    label={task.status.replace('_', ' ')}
                    size="small"
                    sx={{
                        height: 20,
                        fontFamily: MAC_FONT,
                        fontSize: '11px',
                        fontWeight: 500,
                        bgcolor: alpha(statusColor, 0.10),
                        color: statusTextColor,       // .dark shade — WCAG AA on alpha-0.10 bg
                        textTransform: 'capitalize',
                        flexShrink: 0,
                        borderRadius: '6px',
                        '& .MuiChip-label': { px: '8px' },
                    }}
                />
            )}

            {/* Rescheduled chip (hover → who/when). Renders only when the task
                carries a reschedule field — see TaskAttribution.getRescheduleInfo. */}
            <RescheduledChip task={task} />

            {/* Due date / completer / paused — see dueOrStatusEl above. */}
            {dueOrStatusEl}

            {/* Priority chip — only urgent/high */}
            {task.priority && task.priority !== 'normal' && task.priority !== 'low' && (
                <Chip
                    label={task.priority}
                    size="small"
                    sx={{
                        height: 18,
                        fontFamily: MAC_FONT,
                        fontSize: '10px',
                        fontWeight: 500,
                        bgcolor: alpha(priorityColor || MAC.textTertiary, 0.10),
                        color: priorityTextColor || MAC.textSecondary,  // .dark shade — WCAG AA
                        textTransform: 'capitalize',
                        flexShrink: 0,
                        borderRadius: '5px',
                        '& .MuiChip-label': { px: '6px' },
                    }}
                />
            )}

            {/* Checklist counter */}
            {checklistTotal > 0 && (
                <Typography sx={{
                    fontFamily: MAC_FONT,
                    fontSize: '11px',
                    fontWeight: 400,
                    color: checklistDone === checklistTotal ? MAC.success : MAC.textSecondary,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                }}>
                    {checklistDone}/{checklistTotal}
                </Typography>
            )}

            {/* Assignees */}
            {assignees.length > 0 && (
                <AvatarGroup max={3} sx={{ flexShrink: 0, '& .MuiAvatar-root': { width: 20, height: 20, fontSize: '10px', border: '1.5px solid #fff' } }}>
                    {assignees.map((a, i) => {
                        const name = a.name || a.full_name || (a.user ? `${a.user.first_name || ''} ${a.user.last_name || ''}`.trim() : '') || a.email || '';
                        const avatarSrc = a.avatar_url || a.avatar || (a.user && a.user.avatar_url) || '';
                        return (
                            // eslint-disable-next-line react/no-array-index-key
                            <Tooltip key={a.id || i} title={name}>
                                <Avatar src={avatarSrc} sx={{ width: 20, height: 20, fontSize: '10px', bgcolor: 'rgba(0, 122, 255, 0.10)', color: MAC.accent }}>
                                    {(name || '?').charAt(0).toUpperCase()}
                                </Avatar>
                            </Tooltip>
                        );
                    })}
                </AvatarGroup>
            )}
        </Box>
            {rowIsPaused && (
                <Box sx={{ mt: '4px', ml: '26px' }}>
                    <PausedAttribution
                        pauseSource={rowPausedInfo?.pauseSource || (rowPausedInfo?.pausedBy ? { paused_by: rowPausedInfo.pausedBy } : task)}
                        resumeLabel={formatPausedUntil(rowPausedInfo?.pausedUntil)}
                        size={16}
                    />
                </Box>
            )}
            {showLegacyNote && (
                <Typography
                    sx={{
                        fontFamily: MAC_FONT,
                        fontSize: '11px',
                        color: MAC.textSecondary,
                        mt: '4px',
                        ml: '26px',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    Reminder note: {legacyNote}
                </Typography>
            )}
        </Box>
    );
}

TaskRow.propTypes = {
    task: PropTypes.object.isRequired,
    onComplete: PropTypes.func.isRequired,
    onTitleClick: PropTypes.func.isRequired,
    isPaused: PropTypes.bool,
    pausedInfo: PropTypes.shape({
        pausedUntil: PropTypes.string,
        pausedBy: PropTypes.string,
        pauseSource: PropTypes.object,
    }),
    isTemplateTask: PropTypes.bool,
};

TaskRow.defaultProps = {
    isPaused: false,
    pausedInfo: null,
    isTemplateTask: false,
};

// -----------------------------------------------

export default function LeadTaskList({ leadId, practiceId }) {
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const { user: authUser } = useAuth();
    const isSuperuser = !!authUser?.is_superuser;

    const [pendingTasks, setPendingTasks] = useState([]);
    const [completedTasks, setCompletedTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [checklistTaskIds, setChecklistTaskIds] = useState(new Set());
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
    const [templateDetailOpen, setTemplateDetailOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    // Slideout state — clicking an "Other tasks" row opens TaskViewer (V2 slideout).
    const [slideoutOpen, setSlideoutOpen] = useState(false);
    const [selectedTaskId, setSelectedTaskId] = useState(null);
    const selectedTreatmentId = useMemo(() => {
        if (!location?.search) return null;
        return new URLSearchParams(location.search).get('treatmentId');
    }, [location?.search]);

    const { templates } = useSelector((state) => state.taskTemplate);
    const templateList = Array.isArray(templates) ? templates : (templates?.results || []);
    // leadDetail provides lead_treatments so TaskChecklistCard can seed empty
    // treatment sections for treatments that have no active tasks yet (e.g. Perio
    // on a multi-treatment lead where only Composite Bonding has a checklist).
    const { leadDetail } = useSelector((state) => state.lead);

    // Lead detail from Redux so the unified CreateTaskDialog can prefill the
    // lead selector + treatments without re-fetching.
    const followLead = useSelector((state) => state.lead.followLead);
    const dialogDefaultLead = followLead && (String(followLead.id) === String(leadId))
        ? followLead
        : { id: leadId };

    // Load templates when the template picker opens
    useEffect(() => {
        if (templatePickerOpen && practiceId) {
            dispatch(getTemplates(practiceId, ''));
        }
    }, [templatePickerOpen, practiceId]);

    const fetchTasks = useCallback(async () => {
        if (!practiceId || !leadId) return;
        setLoading(true);
        try {
            // Standalone + completed tasks (list shape). Template-checklist
            // groups are fetched + rendered by TaskChecklistCard itself (it owns
            // their completion/menu logic); here we only need the standalone
            // tasks and the completed-task history to interleave into the
            // grouped-by-treatment layout.
            const [pendingRes, completedRes] = await Promise.all([
                callApi(`/api/v1/practices/${practiceId}/tasks/?lead=${leadId}&status__in=pending,in_progress&ordering=due_at&limit=50`),
                callApi(`/api/v1/practices/${practiceId}/tasks/?lead=${leadId}&status=completed&ordering=-completed_at&limit=20`),
            ]);
            if (pendingRes?.status === 200) {
                setPendingTasks(pendingRes.data?.results || []);
            }
            if (completedRes?.status === 200) {
                setCompletedTasks(completedRes.data?.results || []);
            }
        } finally {
            setLoading(false);
        }
    }, [practiceId, leadId]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    // Per-card exit animation. TaskBodyView broadcasts
    // ``tasks-v2:task-completing`` when a checklist completion flips its
    // parent task to ``completed``. We drop the row from the matching
    // local-state bucket immediately so AnimatePresence plays the shared
    // exit variant. The slice refetch is already deferred by TaskBodyView,
    // so the BE-confirmed removal doesn't race the visual unmount.
    useEffect(() => {
        const handler = (e) => {
            const exitingId = String(e?.detail?.taskId || '');
            if (!exitingId) return;
            setPendingTasks((prev) => prev.filter((t) => String(t.id) !== exitingId));
            setCompletedTasks((prev) => prev.filter((t) => String(t.id) !== exitingId));
        };
        // Phase 3 — Undo Snackbar dispatches TASK_RESTORE_EVENT with the
        // pre-completion task snapshot. The snapshot represents a still-
        // pending task (the auto-completion is being reversed), so we
        // re-insert it into pendingTasks. AnimatePresence plays the
        // shared enter variant automatically.
        const restoreHandler = (e) => {
            const task = e?.detail?.task;
            if (!task?.id) return;
            setPendingTasks((prev) => {
                if (prev.some((t) => String(t.id) === String(task.id))) return prev;
                return [task, ...prev];
            });
            // P2-2 M4: refetch so the restored task settles into its
            // correct sort position (and we reconcile against any BE
            // state that diverged from the snapshot).
            fetchTasks();
        };
        // Any sibling surface (complete / pause / resume / add / remove
        // checklist) broadcasts `lead-tasks-v2-changed` on window. Refetch so
        // the grouped-by-treatment view (and its paused/resume metadata) stays
        // current without a route change. We do a full fetchTasks() — not just
        // a local-state splice — because paused state + per-treatment grouping
        // come from the /task-checklists/ groups, which only a refetch refreshes.
        // Skip our own 'lead-task-list' events — mutateAndNotify() already called
        // fetchTasks() locally, so re-fetching here would double up.
        const changedHandler = (e) => { if (e?.detail?.source === 'lead-task-list') return; fetchTasks(); };
        window.addEventListener(TASK_EXIT_EVENT, handler);
        window.addEventListener(TASK_RESTORE_EVENT, restoreHandler);
        window.addEventListener('lead-tasks-v2-changed', changedHandler);
        return () => {
            window.removeEventListener(TASK_EXIT_EVENT, handler);
            window.removeEventListener(TASK_RESTORE_EVENT, restoreHandler);
            window.removeEventListener('lead-tasks-v2-changed', changedHandler);
        };
    // P2-2 M4: include fetchTasks so the restore handler always invokes
    // the latest filter-aware fetch (closure captures the current ref).
    }, [fetchTasks]);

    // Refresh the local list AND tell the Tasks-tab badge (LeadDetailViewBody)
    // that tasks changed — badge-only, no full-page reload (the 'lead-task-list'
    // source gates out the followUpSlice flicker in the badge handler).
    const mutateAndNotify = useCallback(() => {
        fetchTasks();
        window.dispatchEvent(new CustomEvent('lead-tasks-v2-changed', { detail: { source: 'lead-task-list' } }));
    }, [fetchTasks]);

    const handleComplete = (taskId) => {
        dispatch(completeTask(practiceId, taskId, (result) => {
            if (result.success) {
                enqueueSnackbar('Task completed', { variant: 'success' });
                mutateAndNotify();
            }
        }));
    };

    const handleTitleClick = (task) => {
        setSelectedTaskId(String(task.id));
        setSlideoutOpen(true);
    };

    const handleSlideoutClose = () => {
        setSlideoutOpen(false);
        setSelectedTaskId(null);
    };

    // Standalone tasks = /tasks/ rows NOT backed by a template checklist.
    // TaskChecklistCard reports the template-backed task IDs via onGroupsLoaded.
    // These are handed to TaskChecklistCard (groupByTreatment mode), which
    // buckets them into the matching per-treatment section alongside the
    // template checklists and renders each via renderStandaloneRow below.
    const standaloneOpenTasks = pendingTasks.filter((t) => !checklistTaskIds.has(String(t.id)));
    const standaloneCompletedTasks = completedTasks.filter((t) => !checklistTaskIds.has(String(t.id)));

    // Render a standalone task as a TaskRow (working inline complete checkbox →
    // completeTask). Passed into TaskChecklistCard so standalone rows interleave
    // with template checklists inside their treatment section. `completed`
    // standalone rows render read-only inside the global completed collapse.
    const renderStandaloneRow = (task) => (
        <TaskRow task={task} onComplete={handleComplete} onTitleClick={handleTitleClick} />
    );

    return (
        <Box sx={{ fontFamily: MAC_FONT }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontFamily: MAC_FONT, fontWeight: 600, fontSize: '17px', color: MAC.text, letterSpacing: '-0.01em' }}>
                    Tasks
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    {isSuperuser && (
                        <Button
                            size="small"
                            onClick={() => setTemplatePickerOpen(true)}
                            sx={{
                                fontFamily: MAC_FONT,
                                fontSize: '12px',
                                fontWeight: 500,
                                height: 30,
                                textTransform: 'none',
                                color: MAC.text,
                                bgcolor: 'transparent',
                                border: `1px solid ${MAC.border}`,
                                borderRadius: '8px',
                                px: 1.5,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)', borderColor: MAC.borderHover },
                            }}
                        >
                            Apply Template
                        </Button>
                    )}
                    <Button
                        size="small"
                        startIcon={<Iconify icon="eva:plus-fill" width={14} height={14} />}
                        onClick={() => setAddDialogOpen(true)}
                        sx={{
                            fontFamily: MAC_FONT,
                            fontSize: '12px',
                            fontWeight: 500,
                            height: 30,
                            textTransform: 'none',
                            color: '#fff',
                            bgcolor: MAC.accent,
                            borderRadius: '8px',
                            px: 1.5,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#0066D6', boxShadow: 'none' },
                        }}
                    >
                        Add Task
                    </Button>
                </Box>
            </Box>

            {/* Group-by-treatment layout. TaskChecklistCard owns the template
                checklist groups + all their completion/menu logic; we hand it
                the standalone tasks + a row renderer so each treatment section
                shows that treatment's checklists AND its standalone tasks
                interleaved. ONE global completed collapse + the "No treatment"
                section + the empty state all live inside the card in this mode. */}
            <TaskChecklistCard
                practiceId={practiceId}
                leadId={leadId}
                leadData={leadDetail?.id === leadId ? leadDetail : { id: leadId }}
                onGroupsLoaded={(ids) => setChecklistTaskIds(new Set(ids))}
                groupByTreatment
                standaloneOpenTasks={standaloneOpenTasks}
                standaloneCompletedTasks={standaloneCompletedTasks}
                renderStandaloneRow={renderStandaloneRow}
                parentLoading={loading}
                selectedTreatmentId={selectedTreatmentId}
            />

            {/* Add Task Dialog — reuses the full-featured CreateTaskDialog so the
                Tasks tab "+" surface matches the Overview "+" menu (assignees,
                treatment selector, mergeable lead context, etc.). */}
            <CreateTaskDialog
                open={addDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                practiceId={practiceId}
                defaultLead={dialogDefaultLead}
                onSuccess={mutateAndNotify}
            />

            {/* Template Picker — step 1: choose template */}
            <Dialog
                open={templatePickerOpen}
                onClose={() => setTemplatePickerOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle sx={{ pb: 1, fontSize: '15px', fontWeight: 700 }}>
                    Choose a Template
                    <IconButton
                        onClick={() => setTemplatePickerOpen(false)}
                        size="small"
                        sx={{ position: 'absolute', right: 12, top: 12 }}
                    >
                        <Iconify icon="radix-icons:cross-2" width={16} height={16} />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1, pb: 0 }}>
                    {templateList.length === 0 && (
                        <Typography variant="body2" color="text.disabled" sx={{ py: 2 }}>
                            No templates available.
                        </Typography>
                    )}
                    <List dense disablePadding>
                        {templateList.map((tmpl) => (
                            <ListItem key={tmpl.id} disableGutters>
                                <ListItemButton
                                    onClick={() => {
                                        setSelectedTemplate(tmpl);
                                        setTemplatePickerOpen(false);
                                        setTemplateDetailOpen(true);
                                    }}
                                    sx={{ borderRadius: 1, py: 0.75 }}
                                >
                                    <ListItemText
                                        primary={tmpl.name}
                                        secondary={tmpl.category || tmpl.template_type || null}
                                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                                        secondaryTypographyProps={{ variant: 'caption' }}
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setTemplatePickerOpen(false)} size="small" color="inherit">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Template Detail — step 2: preview + apply to this lead */}
            {selectedTemplate && (
                <TemplateDetail
                    open={templateDetailOpen}
                    template={selectedTemplate}
                    practiceId={practiceId}
                    defaultLeadId={leadId}
                    onClose={() => {
                        setTemplateDetailOpen(false);
                        setSelectedTemplate(null);
                    }}
                    onApplied={() => {
                        setTemplateDetailOpen(false);
                        setSelectedTemplate(null);
                        fetchTasks();
                        enqueueSnackbar('Template applied successfully', { variant: 'success' });
                    }}
                />
            )}

            {/* Slideout for Other-tasks rows. Checklist-card tasks open their own
                via TaskChecklistCard's internal TaskViewer; this one owns the rest. */}
            <TaskViewer
                open={slideoutOpen}
                onClose={handleSlideoutClose}
                taskId={selectedTaskId}
                practiceId={practiceId}
                onTaskUpdated={mutateAndNotify}
            />

            {/* P2-2 I1: Undo Snackbar host now mounts once in
                DashboardLayout — removed from per-surface mounts. */}
        </Box>
    );
}

LeadTaskList.propTypes = {
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};
